import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from './ProjectProvider';
import { 
  Plus, 
  Search, 
  Filter, 
  Clock, 
  Star, 
  Users,
  Edit,
  Copy,
  Trash2,
  Eye,
  Image as ImageIcon,
  FolderOpen,
  X
} from 'lucide-react';

const ProjectCatalog = () => {
  const navigate = useNavigate();
  const { projects, deleteProject, cloneProject, loading } = useProjects();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('projectCatalog-sortBy') || 'name';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('projectCatalog-sortOrder') || 'asc';
  });

  // Get unique categories and difficulties for filters
  const categories = [...new Set(projects.map(p => p.category).filter(Boolean))];
  const difficulties = [...new Set(projects.map(p => p.difficulty).filter(Boolean))];

  // Filter projects based on search and filters
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || project.category === filterCategory;
    const matchesDifficulty = !filterDifficulty || project.difficulty === filterDifficulty;
    
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  // Sort projects
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'category':
        aValue = a.category?.toLowerCase() || '';
        bValue = b.category?.toLowerCase() || '';
        break;
      case 'difficulty':
        const difficultyOrder = { 'Beginner': 1, 'Intermediate': 2, 'Advanced': 3 };
        aValue = difficultyOrder[a.difficulty] || 0;
        bValue = difficultyOrder[b.difficulty] || 0;
        break;
      case 'estimatedTime':
        aValue = a.estimated_time?.toLowerCase() || '';
        bValue = b.estimated_time?.toLowerCase() || '';
        break;
      case 'dateCreated':
        aValue = new Date(a.created_at || 0);
        bValue = new Date(b.created_at || 0);
        break;
      case 'linkedClasses':
        aValue = a.linked_classes_count || 0;
        bValue = b.linked_classes_count || 0;
        break;
      default:
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
    }

    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Get main image for a project
  const getMainImage = (project) => {
    if (project.main_image_url && project.main_image_url.trim()) {
      return project.main_image_url;
    }
    if (project.images && project.images.length > 0) {
      return project.images[0];
    }
    return null;
  };

  // Handle project actions
  const handleView = (project) => {
    navigate(`/projects/view/${project.id}`);
  };

  const handleEdit = (project) => {
    navigate(`/projects/edit/${project.id}`);
  };

  const handleClone = async (project) => {
    try {
      await cloneProject(project.id);
    } catch (error) {
      console.error('Error cloning project:', error);
    }
  };

  const handleDelete = async (project) => {
    if (window.confirm(`Are you sure you want to delete "${project.name}"? This action cannot be undone.`)) {
      try {
        await deleteProject(project.id);
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterCategory('');
    setFilterDifficulty('');
    setSortBy('name');
    setSortOrder('asc');
    // Clear persisted sort preferences when clearing filters
    localStorage.removeItem('projectCatalog-sortBy');
    localStorage.removeItem('projectCatalog-sortOrder');
  };

  const activeFiltersCount = [searchTerm, filterCategory, filterDifficulty].filter(Boolean).length;

  // Persist sort preferences
  useEffect(() => {
    localStorage.setItem('projectCatalog-sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('projectCatalog-sortOrder', sortOrder);
  }, [sortOrder]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-lg text-[#3C3C3C] dark:text-gray-200">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FolderOpen className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-200">Project Library</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">{sortedProjects.length} projects • Curriculum content for your classes</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/projects/add')}
            className="bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-[#3C3C3C] dark:text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create Project</span>
          </button>
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search projects by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
              />
            </div>


            {/* Filter Toggle */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 text-sm text-[#3C3C3C] dark:text-gray-300 hover:text-[#7A99D8] dark:hover:text-blue-400 transition-colors"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-[#7A99D8] dark:bg-blue-600 text-white text-xs rounded-full px-2 py-1">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-[#3C3C3C] dark:hover:text-gray-200 transition-colors"
                >
                  Clear all filters
                </button>
              )}
            </div>

            {/* Expandable Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">Category</label>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">Difficulty</label>
                  <select
                    value={filterDifficulty}
                    onChange={(e) => setFilterDifficulty(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                  >
                    <option value="">All Difficulties</option>
                    {difficulties.map(difficulty => (
                      <option key={difficulty} value={difficulty}>{difficulty}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results Summary and Sorting */}
        <div className="flex items-center justify-between mb-6">
          <span className="text-gray-600 dark:text-gray-400">
            Showing {sortedProjects.length} of {projects.length} projects
          </span>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-[#3C3C3C] dark:text-gray-200">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent text-sm"
              >
                <option value="name">Name</option>
                <option value="category">Category</option>
                <option value="difficulty">Difficulty</option>
                <option value="estimatedTime">Estimated Time</option>
                <option value="dateCreated">Date Created</option>
                <option value="linkedClasses">Linked Classes</option>
              </select>
            </div>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="flex items-center gap-1 text-sm text-[#3C3C3C] dark:text-gray-300 hover:text-[#7A99D8] dark:hover:text-blue-400 transition-colors"
            >
              {sortOrder === 'asc' ? '↑' : '↓'} {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
            </button>
          </div>
        </div>

        {/* Projects Grid */}
        {sortedProjects.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
            <FolderOpen className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-200 mb-2">
              {projects.length === 0 ? 'No projects yet' : 'No projects match your filters'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {projects.length === 0 
                ? 'Create your first project to start building your curriculum library.'
                : 'Try adjusting your search terms or filters to find what you\'re looking for.'
              }
            </p>
            {projects.length === 0 && (
              <button
                onClick={() => navigate('/projects/add')}
                className="bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-[#3C3C3C] dark:text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Create Your First Project</span>
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-6 gap-4">
            {sortedProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onView={handleView}
                onEdit={handleEdit}
                onClone={handleClone}
                onDelete={handleDelete}
                getMainImage={getMainImage}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Project Card Component
const ProjectCard = ({ project, onView, onEdit, onClone, onDelete, getMainImage }) => {
  const [showActions, setShowActions] = useState(false);
  const mainImage = getMainImage(project);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return 'bg-[#B7F1B6] text-[#3C3C3C]';
      case 'intermediate': return 'bg-[#FFECC5] text-[#3C3C3C]';
      case 'advanced': return 'bg-[#FFC6C5] text-[#3C3C3C]';
      default: return 'bg-[#D9D9D9] text-[#3C3C3C]';
    }
  };

  return (
    <div 
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Project Image */}
      <div className="relative h-48 bg-gray-100 dark:bg-gray-700">
        {mainImage ? (
          <img
            src={mainImage}
            alt={project.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700" style={{ display: mainImage ? 'none' : 'flex' }}>
          <ImageIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
        </div>

        {/* Quick Actions Overlay */}
        {showActions && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center space-x-2">
            <button
              onClick={(e) => { e.stopPropagation(); onView(project); }}
              className="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-full transition-colors"
              title="View Details"
            >
              <Eye className="w-4 h-4 text-[#3C3C3C] dark:text-gray-200" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(project); }}
              className="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-full transition-colors"
              title="Edit Project"
            >
              <Edit className="w-4 h-4 text-[#3C3C3C] dark:text-gray-200" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onClone(project); }}
              className="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-full transition-colors"
              title="Clone Project"
            >
              <Copy className="w-4 h-4 text-[#3C3C3C] dark:text-gray-200" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(project); }}
              className="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-full transition-colors"
              title="Delete Project"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        )}
      </div>

      {/* Project Info */}
      <div className="p-4" onClick={() => onView(project)}>
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-sm font-medium text-[#3C3C3C] dark:text-gray-200 line-clamp-2 flex-1">
            {project.name}
          </h3>
          {project.difficulty && (
            <span className={`text-xs px-2 py-1 rounded-full ml-2 ${getDifficultyColor(project.difficulty)}`}>
              {project.difficulty}
            </span>
          )}
        </div>

        {project.description && (
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
            {project.description}
          </p>
        )}

        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
          <div className="flex items-center space-x-3">
            {project.estimated_time && (
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>{project.estimated_time}</span>
              </div>
            )}
            {project.category && (
              <span className="bg-[#F5F5F5] dark:bg-gray-700 px-2 py-1 rounded text-[#3C3C3C] dark:text-gray-200">
                {project.category}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCatalog;